'use client';

import React from 'react';
import { Box, Modal, Typography, Button, Stack, keyframes, alpha } from '@mui/material';
import { SwapHoriz, Logout, Business, Person, Warning } from '@mui/icons-material';
import { STATUS, TEXT, BACKGROUND, BORDER, BRAND } from '@/constants/colors';
import { useTranslation } from '@/kit/useTranslation';

// Animations
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -40%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

/**
 * Ultra-modern modal for user type change that requires logout
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {Function} props.onLogout - Callback for logout action
 * @param {string} props.currentType - Current user type ('INDIVIDUAL' or 'BUSINESS')
 * @param {string} props.newType - New user type being switched to
 * @param {boolean} props.loading - Loading state for logout action
 */
export function UserTypeChangeModal({
  open,
  onClose,
  onLogout,
  currentType,
  newType,
  loading = false,
}) {
  const { t } = useTranslation();
  const isToBusinessType = String(newType).toUpperCase() === 'BUSINESS';

  return (
    <Modal
      open={open}
      onClose={loading ? undefined : onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      BackdropProps={{
        sx: {
          backgroundColor: alpha(TEXT.primary, 0.75),
          backdropFilter: 'blur(8px)',
          animation: `${fadeIn} 0.2s ease-out`,
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: 'calc(100% - 32px)', sm: 420 },
          maxWidth: 420,
          bgcolor: BACKGROUND.paper,
          borderRadius: 4,
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          animation: `${slideUp} 0.35s ease-out`,
          outline: 'none',
        }}
      >
        {/* Header with gradient */}
        <Box
          sx={{
            background: isToBusinessType
              ? `linear-gradient(135deg, ${STATUS.warning} 0%, #d97706 100%)`
              : `linear-gradient(135deg, ${BRAND.accent} 0%, #4f46e5 100%)`,
            py: 3.5,
            px: 3,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background pattern */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0.1,
              background: `radial-gradient(circle at 20% 50%, white 2px, transparent 2px),
                           radial-gradient(circle at 80% 50%, white 2px, transparent 2px)`,
              backgroundSize: '40px 40px',
            }}
          />

          {/* Animated icon swap */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              mb: 2,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                bgcolor: alpha(TEXT.inverse, 0.2),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: `${pulse} 2s ease-in-out infinite`,
              }}
            >
              {isToBusinessType ? (
                <Person sx={{ fontSize: 26, color: TEXT.inverse }} />
              ) : (
                <Business sx={{ fontSize: 26, color: TEXT.inverse }} />
              )}
            </Box>

            <SwapHoriz
              sx={{
                fontSize: 28,
                color: alpha(TEXT.inverse, 0.8),
              }}
            />

            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                bgcolor: BACKGROUND.paper,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                animation: `${pulse} 2s ease-in-out infinite`,
                animationDelay: '0.3s',
              }}
            >
              {isToBusinessType ? (
                <Business sx={{ fontSize: 26, color: STATUS.warningDark }} />
              ) : (
                <Person sx={{ fontSize: 26, color: BRAND.accent }} />
              )}
            </Box>
          </Box>

          <Typography
            variant="h6"
            sx={{
              color: TEXT.inverse,
              fontWeight: 800,
              fontSize: '1.2rem',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {t('userTypeChange.switchToAccount', {
              type: isToBusinessType ? t('nav.business') : t('nav.individual'),
            })}
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {/* Warning card */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              p: 2,
              borderRadius: 2.5,
              bgcolor: alpha(STATUS.warning, 0.08),
              border: '1px solid',
              borderColor: alpha(STATUS.warning, 0.2),
              mb: 3,
            }}
          >
            <Warning
              sx={{
                color: STATUS.warning,
                fontSize: 22,
                mt: 0.25,
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  color: STATUS.pending.text,
                  mb: 0.5,
                }}
              >
                {t('userTypeChange.logoutRequired')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  color: STATUS.warningDark,
                  lineHeight: 1.5,
                }}
              >
                {t('userTypeChange.logoutRequiredMessage')}
              </Typography>
            </Box>
          </Box>

          {/* What happens section */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.9rem',
                color: TEXT.primary,
                mb: 1.5,
              }}
            >
              {t('userTypeChange.whatHappensNext')}
            </Typography>
            <Stack spacing={1.5}>
              {[
                t('userTypeChange.step1'),
                t('userTypeChange.step2'),
                t('userTypeChange.step3'),
                isToBusinessType
                  ? t('userTypeChange.step4Business')
                  : t('userTypeChange.step4Individual'),
              ].map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: isToBusinessType
                        ? alpha(STATUS.warning, 0.12)
                        : alpha(BRAND.accent, 0.12),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: isToBusinessType ? STATUS.warningDark : BRAND.accent,
                      flexShrink: 0,
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: TEXT.secondary,
                      lineHeight: 1.4,
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Action buttons */}
          <Stack direction="row" spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              disabled={loading}
              sx={{
                py: 1.5,
                fontWeight: 700,
                fontSize: '0.9rem',
                borderRadius: 2.5,
                borderColor: BORDER.light,
                color: TEXT.secondary,
                textTransform: 'none',
                '&:hover': {
                  borderColor: BORDER.default,
                  bgcolor: BACKGROUND.subtle,
                },
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={onLogout}
              disabled={loading}
              startIcon={!loading && <Logout sx={{ fontSize: 20 }} />}
              sx={{
                py: 1.5,
                fontWeight: 700,
                fontSize: '0.9rem',
                borderRadius: 2.5,
                textTransform: 'none',
                background: loading
                  ? `linear-gradient(90deg, 
                      ${isToBusinessType ? STATUS.warning : BRAND.accent} 0%, 
                      ${isToBusinessType ? '#fbbf24' : '#8b5cf6'} 50%, 
                      ${isToBusinessType ? STATUS.warning : BRAND.accent} 100%)`
                  : isToBusinessType
                    ? `linear-gradient(135deg, ${STATUS.warning} 0%, ${STATUS.warningDark} 100%)`
                    : `linear-gradient(135deg, ${BRAND.accent} 0%, #4f46e5 100%)`,
                backgroundSize: loading ? '200% auto' : 'auto',
                animation: loading ? `${shimmer} 1.5s linear infinite` : 'none',
                boxShadow: isToBusinessType
                  ? '0 4px 20px rgba(245, 158, 11, 0.35)'
                  : '0 4px 20px rgba(99, 102, 241, 0.35)',
                '&:hover': {
                  background: isToBusinessType
                    ? `linear-gradient(135deg, ${STATUS.warningDark} 0%, #b45309 100%)`
                    : 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                  boxShadow: isToBusinessType
                    ? '0 6px 25px rgba(245, 158, 11, 0.45)'
                    : '0 6px 25px rgba(99, 102, 241, 0.45)',
                },
                '&:disabled': {
                  color: TEXT.inverse,
                },
              }}
            >
              {loading ? t('userTypeChange.loggingOut') : t('userTypeChange.logoutAndSwitch')}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
}

export default UserTypeChangeModal;
